import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { NEVER } from 'rxjs';
import { cloneDeep } from 'lodash';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { ItinerarySearchResults } from '@apps/main/modules/tripPlannerModel/itinerarySearchResults';
import { SmartSearchService } from '@apps/main/modules/search/smartSearchService';
import type { PriceCalendarState } from '@apps/main/new-desktop/modules/priceCalendar';
import { getPriceCalendarState } from '@apps/main/new-desktop/core/stores/priceCalendar/priceCalendarStoreLogic';
import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';
import {
  useItineraryStore,
  usePriceCalendarStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import type { PriceRibbonMonth } from '@apps/main/new-desktop/modules/priceRibbon';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';
import { adaptRecipeIngredientsToItinerarySearchCriteria } from '@apps/main/modules/criteria/itinerarySearchCriteria/itinerariesCriteria';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import { DateFormat, SemboDate, weekdays } from '@libs/sembo-dates';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import type {
  ItinerarySearchCriteria,
  ItinerarySearchUserCriteria,
} from '@libs/trip-planner-interfaces';

const SEARCH_SERVICE = new SmartSearchService();

function getMonthStartAndEndDates(date: Date): {
  startDate: Date;
  endDate: Date;
  selectedDate: undefined;
} {
  const year = date.getFullYear();
  const month = date.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  return { startDate, endDate, selectedDate: undefined };
}

export const usePeekPriceStore = defineStore('PeekPriceHotel', () => {
  const { t } = i18n.global;

  const preferredTimeStore = usePreferredTimeStore();
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();
  const itineraryStore = useItineraryStore();
  const priceCalendarStore = usePriceCalendarStore();
  const criteriaStore = useCriteriaStore();

  // State
  const itineraryDatesRef = ref<DateWithItinerary[]>([]);
  const selectedMonthISODateRef = ref<string | undefined>(undefined);
  const ingredientIndexRef = ref<number | undefined>(undefined);
  const hotelCodeRef = ref<string | undefined>(undefined);
  const selectedDateRef = ref<Date | undefined>(undefined);

  // Actions
  function loadPrices(ingredientIndex: number, hotelCode: string): void {
    itineraryDatesRef.value = [];
    ingredientIndexRef.value = ingredientIndex;
    hotelCodeRef.value = hotelCode;

    const recipeCopy: RecipeMainIngredient[] = cloneDeep(recipeStore.recipe);
    const ingredient = recipeCopy[ingredientIndex];

    if (!ingredient) {
      return;
    }

    if (!isHotelIngredient(ingredient)) {
      return;
    }

    ingredient.heartedHotels = [{ code: hotelCode }];

    const dates = selectedMonthISODateRef.value
      ? getMonthStartAndEndDates(new Date(selectedMonthISODateRef.value))
      : {
          startDate: preferredTimeStore.startSearchDate,
          endDate: preferredTimeStore.endSearchDate,
          selectedDate: preferredTimeStore.preferredDay,
        };

    const userCriteria: ItinerarySearchUserCriteria = {
      ingredients: adaptRecipeIngredientsToItinerarySearchCriteria(recipeCopy),
      pax: recipeStore.pax.rooms,
      endSearchDate: dates.endDate,
      startSearchDate: dates.startDate,
      selectedDate: dates.selectedDate,
      sessionId: tripStore.sessionId,
    };
    const criteria: ItinerarySearchCriteria = {
      userCriteria,
      businessCriteria: criteriaStore.businessCriteria,
    };
    const results = new ItinerarySearchResults(SEARCH_SERVICE, criteria, NEVER);
    results.itineraries.subscribe((i) => {
      const itinerary = { date: i.date, itinerary: i };
      const allItineraries = [...itineraryDatesRef.value, itinerary];
      allItineraries.sort((a, b) => a.date.getTime() - b.date.getTime());
      itineraryDatesRef.value = [...allItineraries];
    });
  }

  function selectMonth(date: Date): void {
    const semboDate = new SemboDate(date);
    selectedMonthISODateRef.value = semboDate.format(DateFormat.ISO);
  }

  function preselectDate(date: Date): void {
    selectedDateRef.value = date;
  }

  function clean(): void {
    itineraryDatesRef.value = [];
    selectedMonthISODateRef.value = undefined;
    ingredientIndexRef.value = undefined;
    hotelCodeRef.value = undefined;
    selectedDateRef.value = undefined;
  }

  function confirm(): void {
    if (
      !selectedDateRef.value ||
      ingredientIndexRef.value === undefined ||
      !hotelCodeRef.value
    ) {
      return;
    }

    const ingredientId =
      recipeStore.recipe[ingredientIndexRef.value]?.ingredientId;

    if (!ingredientId) {
      return;
    }

    preferredTimeStore.setPreferredDay(selectedDateRef.value);

    const heartedHotel = { code: hotelCodeRef.value };
    tripStore.replaceHeartedHotel(heartedHotel, ingredientId);
    clean();
  }

  // Getters
  const months = computed<PriceRibbonMonth[]>(() => {
    if (!selectedMonthISODateRef.value) {
      return priceCalendarStore.priceRibbonMonths;
    }

    return priceCalendarStore.priceRibbonMonths.map(
      (month: PriceRibbonMonth) => {
        const isExtended =
          new SemboDate(month.firstDayInMonthISO).format(
            DateFormat.ShortMonthAndYear
          ) ===
          new SemboDate(selectedMonthISODateRef.value).format(
            DateFormat.ShortMonthAndYear
          );

        return { ...month, isExtended };
      }
    );
  });

  const selectedMonth = computed<string>(
    () =>
      selectedMonthISODateRef.value ?? priceCalendarStore.selectedMonthOption.id
  );

  const priceCalendarState = computed<PriceCalendarState>(() =>
    getPriceCalendarState({
      weekdays: weekdays(DateFormat.ShortDayName),
      itineraryDates: itineraryDatesRef.value,
      isSelectedItinerary: isSelectedItinerary,
      selectedItinerary: itineraryStore.selectedItinerary,
      alternativeProductMessage: t('planner.alternative_avail'),
      noAvailabilityMessage: t('planner.no_avail'),
      forceLoader: isFeatureToggleActive('loader'),
    })
  );

  const hotelName = computed<string | undefined>(() => {
    if (ingredientIndexRef.value === undefined || !hotelCodeRef.value) {
      return;
    }

    const ingredientId =
      recipeStore.recipe[ingredientIndexRef.value]?.ingredientId;

    if (!ingredientId) {
      return;
    }

    const staticData = tripStore.getStaticInfo(
      hotelCodeRef.value,
      ingredientId
    );

    return staticData?.name;
  });

  // Watchers
  watch(selectedMonthISODateRef, () => {
    if (
      ingredientIndexRef.value !== undefined &&
      hotelCodeRef.value !== undefined
    ) {
      loadPrices(ingredientIndexRef.value, hotelCodeRef.value);
    }
  });

  function isSelectedItinerary(date: Date): boolean {
    if (selectedDateRef.value) {
      return new SemboDate(date).isSameDay(selectedDateRef.value);
    }

    if (preferredTimeStore.preferredDay) {
      return new SemboDate(date).isSameDay(preferredTimeStore.preferredDay);
    }

    return false;
  }

  return {
    // Actions
    clean,
    confirm,
    loadPrices,
    preselectDate,
    selectMonth,

    // Getters
    hotelName,
    months,
    priceCalendarState,
    selectedMonth,
  };
});
